import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { CopyButton } from "@/components/copy-button";
import { StatusBadge } from "@/components/status-badge";
import { formatStatusLabel } from "@/lib/utils";
import { getPlatformWallets } from "@/api/platform-wallets";
import { type PlatformWalletBalance, type PlatformWalletItem } from "@/api/types";

// Real implementation of Task 28 Part C, replacing the Task-22 thin
// placeholder: the two platform vaults (gas station, revenue) plus the
// accrued-but-uncollected platform fees table (FINANCE_VIEWER). Single
// `useQuery` — `GET /v1/platform-wallets` returns everything in one call, no
// pagination/filters. Display-only: zero mutating controls anywhere on this
// page, per spec.
//
// `WALLET_ROLE_LABEL` is a small local label map, not a `status-colors.ts`
// entry — `role` isn't a domain-enum badge, just a plain heading for exactly
// two fixed cards.

export const Route = createFileRoute("/_app/platform-wallets")({
  component: PlatformWalletsPage,
});

// Feature 027: the GAS vault now has its own dedicated page
// (`_app.gas-station.tsx`) with funding-floor controls, so it's filtered out
// of the grid below — REVENUE only. `PlatformWalletItem["role"]` still
// includes 'GAS' (backoffice-api's response shape is unchanged, just no
// longer rendered here), so `WALLET_ROLE_LABEL["GAS"]` is kept as
// defensive/dead code rather than narrowing the type.
const WALLET_ROLE_LABEL: Record<PlatformWalletItem["role"], string> = {
  GAS: "Gas station",
  REVENUE: "Revenue vault",
};

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

/** "Unrecognized asset" when the Fireblocks asset id doesn't match any local `Asset` row — never dropped. */
function AssetCell({ balance }: { balance: PlatformWalletBalance }) {
  if (!balance.symbol || !balance.network) {
    return <span className="text-muted-foreground">Unrecognized asset</span>;
  }
  return <AssetLabel symbol={balance.symbol} network={balance.network} />;
}

// ─── one wallet card ─────────────────────────────────────────────────────────

function WalletCard({ wallet }: { wallet: PlatformWalletItem }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{WALLET_ROLE_LABEL[wallet.role]}</CardTitle>
        <CardDescription className="flex items-center gap-1">
          <span className="font-mono text-xs">{wallet.vaultId}</span>
          <CopyButton value={wallet.vaultId} label="Copy vault id" />
        </CardDescription>
        <p className="text-xs text-muted-foreground">Fetched: {formatTimestamp(wallet.fetchedAt)}</p>
      </CardHeader>
      <CardContent>
        {wallet.degraded ? (
          // Degraded state: no balances table underneath (there are none —
          // `balances` is `[]` on a degraded read) — a clear warning banner
          // instead, distinguishing "connector unreachable" from "genuinely
          // zero balances".
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
            Unable to fetch live balance — the custody connector is unreachable or timed out.
          </div>
        ) : wallet.balances.length === 0 ? (
          <p className="text-sm text-muted-foreground">No balances.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-xs font-semibold text-muted-foreground">
                  <th className="px-4 py-2.5 font-semibold">Asset</th>
                  <th className="px-4 py-2.5 font-semibold">Fireblocks asset id</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Available</th>
                </tr>
              </thead>
              <tbody>
                {wallet.balances.map((balance) => (
                  <tr key={balance.fireblocksAssetId} className="border-t">
                    <td className="px-4 py-2">
                      <AssetCell balance={balance} />
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {balance.fireblocksAssetId}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Amount native={balance.available} symbol={balance.symbol ?? ""} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── platform-wallets page ───────────────────────────────────────────────────

function PlatformWalletsPage() {
  const { data, isPending, isError } = useQuery({
    queryKey: ["platform-wallets"],
    queryFn: getPlatformWallets,
  });

  if (isPending) {
    return <p className="text-sm text-muted-foreground">Loading platform wallets…</p>;
  }
  if (isError || !data) {
    return <p className="text-sm text-muted-foreground">Failed to load platform wallets.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        {data.wallets
          .filter((wallet) => wallet.role !== "GAS")
          .map((wallet) => (
            <WalletCard key={wallet.vaultId} wallet={wallet} />
          ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Accrued platform fees</CardTitle>
          <CardDescription>
            Every asset anyone has ever earned fees on — what a future consolidation would sweep.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.accruedFees.length === 0 ? (
            <p className="text-sm text-muted-foreground">No accrued fees yet.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted text-left text-xs font-semibold text-muted-foreground">
                    <th className="px-4 py-2.5 font-semibold">Asset</th>
                    <th className="px-4 py-2.5 font-semibold">Network</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.accruedFees.map((fee) => (
                    <tr key={fee.assetId} className="border-t">
                      <td className="px-4 py-2 font-medium">{fee.symbol}</td>
                      <td className="px-4 py-2">
                        <StatusBadge label={formatStatusLabel(fee.network)} color="neutral" />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Amount native={fee.balanceNative} symbol={fee.symbol} usd={fee.balanceUsd} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
